import { observable, action } from 'mobx';
import { isEmpty, uniq } from 'lodash';

import { newRate, locateValue } from '@tool/index';

const moDetail = observable({
  allData: <any[]>[],
  tableHead: <any[]>[],
  tableList: <any[]>[],
  years: <any[]>[],
  currentYear: <number | string>'',
});

/**
 * @description 根据年份处理和转换薪资数据，生成用于表格展示的列表。
 *              此函数将核心处理逻辑集中在一起，提高了复用性。
 * @param {string | number} year - 需要处理的数据年份。
 * @param {any[]} data - 完整的原始数据数组。
 * @returns {any[]} - 供表格渲染的列表数据。
 */
const processDataForYear = (year: string | number, data: any[]): any[] => {
  if (isEmpty(data) || !year) return [];

  // 1. 筛选出指定年份的数据
  const dataForYear = data.filter(item => item.date.toString() === year.toString());

  // 2. 使用 Map, 以 "一级-二级-三级职能" 的组合字符串为 key 进行去重。
  //    这解决了不同职能层级下的同名三级职能（如“生产”和“供应链”下的“物料管理”）被错误合并的问题。
  const categoryMap = new Map();
  for (const item of dataForYear) {
    const uniqueKey = `${item.category_lv1}-${item.category_lv2}-${item.category_lv3}`;
    if (!categoryMap.has(uniqueKey)) {
      categoryMap.set(uniqueKey, item);
    }
  }
  const uniqueDataForYear = Array.from(categoryMap.values());

  // 3. 将去重后的数据转换为表格需要的格式。
  //    原实现使用多次循环和 push 操作构建 list，可读性差且易错。
  //    优化后使用 .map() 一次性转换，代码更清晰、高效。
  const tableList = uniqueDataForYear.map(child => {
    // 使用一个结构化的 "dict" (JS 对象) 来清晰地定义一行数据，
    // 这比依赖脆弱的数组下标去取值要健壮得多。
    const rowData = {
      category_lv1: child.category_lv1,
      category_lv2: child.category_lv2,
      category_lv3: child.category_lv3,
      total_company_count: locateValue(child.total_company_count),
      total_company_count_change: newRate(child.total_company_count_change),
      total_amount: locateValue(child.total_amount),
      total_amount_change: newRate(child.total_amount_change),
      total_salary_change: newRate(child.total_salary_change),
      segment_company_count: locateValue(child.segment_company_count),
      segment_amount: locateValue(child.segment_amount),
      segment_amount_change: newRate(child.segment_amount_change),
      segment_salary_change: newRate(child.segment_salary_change),
    };

    return {
      title: child.category,
      // 为了保持与旧视图组件（grade-table.tsx）的兼容性，
      // 我们仍然按表格列的固定顺序将对象转换为数组。
      arr: [
        rowData.category_lv1,
        rowData.category_lv2,
        rowData.category_lv3,
        rowData.total_company_count,
        rowData.total_company_count_change,
        rowData.total_amount,
        rowData.total_amount_change,
        rowData.total_salary_change,
        rowData.segment_company_count,
        rowData.segment_amount,
        rowData.segment_amount_change,
        rowData.segment_salary_change,
      ],
    };
  });

  return tableList;
};


const initDetailRate = action((res: any) => {
  // 设置固定的表头
  moDetail.tableHead = [
    { title: '一级职能' },
    { title: '二级职能' },
    { title: '三级职能' },
    {
      title: '所有公司的薪酬变化',
      child: ['公司数', '公司数变化率', '样本数', '样本数变化率', '涨薪率'],
    },
    {
      title: '相同公司相同职位的薪酬变化',
      child: ['公司数', '样本数', '样本数变化率', '涨薪率'],
    },
  ];

  if (isEmpty(res)) {
    moDetail.allData = [];
    moDetail.years = [];
    moDetail.currentYear = '';
    moDetail.tableList = [];
    return;
  };

  // 存储原始数据
  moDetail.allData = res;

  // 从数据中提取并设置所有唯一的年份
  const years: (string | number)[] = uniq(res.map((item: any) => item.date));
  moDetail.years = years;

  // 确定当前年份，优先从 localStorage 获取，并做有效性检查
  const savedYear = localStorage.getItem('categoryDetailYear');
  const lastYear = years.length > 0 ? years[years.length - 1] : '';
  
  // 显式声明 currentYear 的类型以解决 TypeScript 类型推断问题
  let currentYear: string | number;

  if (savedYear && years.includes(savedYear)) {
    currentYear = savedYear;
  } else {
    currentYear = lastYear;
  }
  
  if (currentYear) {
    localStorage.setItem('categoryDetailYear', currentYear.toString());
  }
  moDetail.currentYear = currentYear;

  // 根据当前年份，调用核心处理函数生成表格数据
  moDetail.tableList = processDataForYear(currentYear, res);
});

const changeYear = action((value: number | string) => {
  // 更新 localStorage 中的年份
  localStorage.setItem('categoryDetailYear', value.toString());
  moDetail.currentYear = value;

  // 使用新的年份，调用核心处理函数重新生成表格数据
  moDetail.tableList = processDataForYear(value, moDetail.allData);
});

export default moDetail;
export {
  initDetailRate, changeYear,
};
